import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { IButtonTertiaryProps } from "@/Interfaces/interface.buttons";
import { ClipLoader } from "react-spinners";

const MotionButton = motion.button;
const MotionAnchor = motion.a;

// Size configurations (same as ButtonSecondary)
const sizeMap = {
  xs: {
    padding: "px-2 py-1",
    text: "text-xs",
    iconSize: "w-3 h-3",
  },
  sm: {
    padding: "px-3 py-1.5",
    text: "text-sm",
    iconSize: "w-4 h-4",
  },
  md: {
    padding: "px-4 py-2",
    text: "text-base",
    iconSize: "w-5 h-5",
  },
  lg: {
    padding: "px-5 py-2.5",
    text: "text-lg",
    iconSize: "w-6 h-6",
  },
  xl: {
    padding: "px-6 py-3",
    text: "text-xl",
    iconSize: "w-7 h-7",
  },
};

// Border radius configurations (same as ButtonSecondary)
const borderRadiusMap = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
};

// Variant-based styling configurations for border buttons
const variantStylesMap = {
  basic: {
    enabled:
      "bg-transparent text-indigo-500 border-2 border-indigo-500 hover:bg-indigo-50 hover:border-indigo-600 dark:text-indigo-400 dark:border-indigo-500 dark:hover:bg-indigo-900/20 dark:hover:border-indigo-400",
    disabled:
      "bg-transparent text-gray-400 border-2 border-gray-300 cursor-not-allowed pointer-events-none dark:text-gray-500 dark:border-gray-600",
  },
  cancel: {
    enabled:
      "bg-transparent text-red-600 border-2 border-red-500 hover:bg-red-50 hover:border-red-600 dark:text-red-400 dark:border-red-500 dark:hover:bg-red-900/20 dark:hover:border-red-400",
    disabled:
      "bg-transparent text-red-300 border-2 border-red-300 cursor-not-allowed pointer-events-none dark:text-red-500 dark:border-red-700",
  },
  info: {
    enabled:
      "bg-transparent text-blue-600 border-2 border-blue-500 hover:bg-blue-50 hover:border-blue-600 dark:text-blue-400 dark:border-blue-500 dark:hover:bg-blue-900/20 dark:hover:border-blue-400",
    disabled:
      "bg-transparent text-blue-300 border-2 border-blue-300 cursor-not-allowed pointer-events-none dark:text-blue-500 dark:border-blue-700",
  },
  disabled: {
    enabled:
      "bg-transparent text-gray-400 border-2 border-gray-300 cursor-not-allowed pointer-events-none dark:text-gray-500 dark:border-gray-600",
    disabled:
      "bg-transparent text-gray-400 border-2 border-gray-300 cursor-not-allowed pointer-events-none dark:text-gray-500 dark:border-gray-600",
  },
};

const ButtonTertiary: React.FC<IButtonTertiaryProps> = ({
  className = "",
  disabled,
  loading = false,
  icon,
  text,
  children,
  href,
  sizeClass,
  borderRadius,
  variant = "basic",
  onClick,
  type = "button",
  form,
  name,
  value,
}) => {
  const isDisabled = disabled || loading || variant === "disabled";
  const contentRef = useRef<HTMLSpanElement>(null);
  const [contentWidth, setContentWidth] = useState<number | null>(null);

  useEffect(() => {
    if (!loading && contentRef.current) {
      const width = contentRef.current.offsetWidth;
      setContentWidth(width);
    }
  }, [loading, text, children, icon]);
  const baseStyles =
    "inline-flex items-center justify-center text-sm font-semibold font-inter transition-all duration-200 shadow-sm relative group focus:outline-none hover:scale-105";

  const variantStyles = variantStylesMap[variant] || variantStylesMap.basic;
  const currentVariantStyles = isDisabled
    ? variantStyles.disabled
    : variantStyles.enabled;

  const sizeStyles = sizeMap[sizeClass || "md"] || sizeMap.md;
  const radiusStyles =
    borderRadiusMap[borderRadius || "md"] || borderRadiusMap.md;

  const combinedClassName = [
    baseStyles,
    currentVariantStyles,
    sizeStyles.padding,
    sizeStyles.text,
    radiusStyles,
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const content = (
    <span
      ref={contentRef}
      className="relative z-10 flex items-center gap-1 sm:gap-2 justify-center"
      style={
        loading && contentWidth ? { width: `${contentWidth}px` } : undefined
      }
    >
      {loading && (
        <ClipLoader loading={true} size={20} color={sizeStyles.iconSize} />
      )}
      {!loading && icon && <span>{icon}</span>}
      {text || children}
    </span>
  );

  if (href) {
    return (
      <MotionAnchor
        href={href}
        className={combinedClassName}
        whileTap={!isDisabled ? { scale: 0.95 } : {}}
        aria-disabled={isDisabled}
        tabIndex={isDisabled ? -1 : undefined}
        onClick={(e) => {
          if (isDisabled) e.preventDefault();
        }}
      >
        {content}
      </MotionAnchor>
    );
  }

  return (
    <MotionButton
      className={combinedClassName}
      disabled={isDisabled}
      onClick={onClick}
      type={type}
      form={form}
      name={name}
      value={value}
      whileTap={!isDisabled ? { scale: 0.95 } : {}}
    >
      {content}
    </MotionButton>
  );
};

export default ButtonTertiary;
